import React, { useState } from "react";
import { UIBrandedFareOrFlexTicket, UIFetchStatus, UIFlightData } from "@flights/types";
import { Accordion, Button, Card, SkeletonLoader, Stack, Text, useTheme } from "@bookingcom/bui-react";
import FareFeaturesIcons from "../../../../branded-fares/components/FareFeatureIcons/FareFeatureIcons";
import FareCardPrice from "./FareCardPrice";
import FareFeatures from "../../../../branded-fares/components/FareFeatures";
import { t, useI18n } from "@bookingcom/lingojs-react";
import useGoToFlightDetails from "../../../../branded-fares/hooks/useGoToFlightDetails";
import Frame from "../../Frame";
import {
  areExcludedFeaturesRequiredByAirline,
  areSellableFeaturesRequiredByAirline
} from "@flights/ancillaries-universal";
import { formatFareName } from "@flights/branded-fares";
import flights_apex_web_show_sellable_and_excluded_seats from "../../../../utils/experiments/apex/flights_apex_web_show_sellable_and_excluded_seats";

type FareCardProps = {
  fareOffer: UIBrandedFareOrFlexTicket;
  baseOffer: UIFlightData;
  fetchStatus: UIFetchStatus;
  index?: number;
  hasSellableBag?: boolean;
};

const FareCard = ({ fareOffer, baseOffer, fetchStatus, index, hasSellableBag }: FareCardProps) => {
  const { brandedFareInfo } = fareOffer;
  const [showDetails, setShowDetails] = useState(false);
  const i18n = useI18n();
  const goToFlightDetails = useGoToFlightDetails();
  const theme = useTheme();
  const sellableFeaturesRequiredByAirline = areSellableFeaturesRequiredByAirline(baseOffer.segments);
  const excludedFeaturesRequiredByAirline = areExcludedFeaturesRequiredByAirline(baseOffer.segments);

  if (showDetails) {
    flights_apex_web_show_sellable_and_excluded_seats.trackStages(true, [{ brandedFareInfo }]);
  }

  return (
    <Card>
      <div
        role="button"
        tabIndex={0}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <Accordion
          active={showDetails}
          onOpen={() => {
            setShowDetails(true);
          }}
          onClose={() => setShowDetails(false)}
          titleContent={
            <Stack>
              {fetchStatus === "loading" ? (
                <Stack gap={6} direction="row">
                  <Stack.Item grow>
                    <SkeletonLoader />
                  </Stack.Item>
                  <SkeletonLoader width={theme.units.spacing_12x} />
                </Stack>
              ) : (
                <Text variant="strong_1">{formatFareName(brandedFareInfo?.fareName)}</Text>
              )}
              <FareCardPrice fareOffer={fareOffer} baseOffer={baseOffer} />
              {!showDetails && (
                <FareFeaturesIcons
                  brandedFareInfo={brandedFareInfo}
                  limit={7}
                  isLarge={true}
                  isCompact={true}
                  isPreCheckAndPay={true}
                />
              )}
            </Stack>
          }
        >
          <FareFeatures
            brandedFareInfo={brandedFareInfo}
            sellableFeaturesRequiredByAirline={sellableFeaturesRequiredByAirline}
            excludedFeaturesRequiredByAirline={excludedFeaturesRequiredByAirline}
            showHeader={true}
            isPreCheckAndPay={true}
            areShown={showDetails}
            travellers={baseOffer.travellerPrices.length}
            hasSellableBag={hasSellableBag}
          />

          <Frame pt={2}>
            <Button
              onClick={() => {
                goToFlightDetails(fareOffer.token, baseOffer, index);
              }}
              variant="secondary"
              wide
            >
              {i18n.trans(t("flights_apex_bf_sr_bf_card_cta"))}
            </Button>
          </Frame>
        </Accordion>
      </div>
    </Card>
  );
};

export default FareCard;
